<div class="dialog-content">
    <div class="users-toolbar">
        <mat-form-field class="search-field" appearance="fill" subscriptSizing="dynamic">
            <mat-label>Zoeken</mat-label>
            <input
                [value]="search()"
                (input)="onSearchChange($any($event.target).value)"
                matInput
                type="search"
                placeholder="Zoek op naam of e-mailadres" />
        </mat-form-field>

        @if (manageInternalUsers) {
            <mat-form-field class="view-field" appearance="fill" subscriptSizing="dynamic">
                <mat-label>Weergave</mat-label>
                <mat-select
                    [value]="view()"
                    (selectionChange)="onViewChange($event.value)"
                    panelClass="users-select-panel">
                    <mat-option value="default">Standaard</mat-option>

                    @if (manageExternalUsers && hasExternal()) {
                        <mat-option value="external">Extern</mat-option>
                    }

                    <mat-option value="former">Uit dienst</mat-option>
                </mat-select>
            </mat-form-field>
        }

        <mat-form-field class="roles-field" appearance="fill" subscriptSizing="dynamic">
            <mat-label>Rollen</mat-label>
            <mat-select
                [value]="selectedRoleKeys()"
                (selectionChange)="onRoleFilterChange($event.value)"
                multiple
                panelClass="users-roles-panel">
                <mat-select-trigger>
                    {{ selectedRoleLabel() }}
                </mat-select-trigger>

                @for (role of availableRoles(); track role.key) {
                    <mat-option [value]="role.key">
                        {{ role.name }}
                    </mat-option>
                }
            </mat-select>

            @if (selectedRoleKeys().length) {
                <button
                    (click)="clearRoleFilter($event)"
                    matSuffix
                    mat-icon-button
                    type="button"
                    aria-label="Rollenfilter wissen">
                    <mat-icon fontSet="fas" fontIcon="fa-times"></mat-icon>
                </button>
            }
        </mat-form-field>

        <mat-slide-toggle
            class="deleted-toggle"
            [checked]="showDeleted()"
            (change)="onShowDeletedChange($event.checked)">
            Toon verwijderde gebruikers
        </mat-slide-toggle>

        <span class="toolbar-spacer"></span>

        @if (manageExternalUsers && hasExternal()) {
            <button (click)="openAddUserModal()" mat-raised-button color="primary">
                <mat-icon fontSet="fas" fontIcon="fa-plus"></mat-icon>
                Gebruiker toevoegen
            </button>
        }

        @if (manageInternalUsers) {
            <button [disabled]="isSyncing()" (click)="sync()" mat-raised-button color="primary">
                @if (isSyncing()) {
                    <i class="fas fa-spinner fa-spin"></i>
                }
                Synchroniseren met AFAS
            </button>
        }
    </div>

    <table class="table table-hover table-last-col">
        <thead>
            <tr>
                <th>Naam</th>
                <th>E-mailadres</th>
                <th>Groep</th>
                <th>Klantgebieden</th>
                <th>Projecten</th>

                @if (environment.defaultStand) {
                    <th>Standplaats</th>
                }

                @if (environment.travelTime) {
                    <th width="180">Reistijd werkdagen / weekend</th>
                }

                @if (environment.employeeEmployees) {
                    <th>Standaard personeel</th>
                }

                <th width="180">Urencontrole door</th>

                <th></th>
            </tr>
        </thead>

        <tbody>
            @for (user of users(); track user.id) {
                <tr class="curpoint" [class.deleted]="user.deleted_at || user.enddate" (click)="openUser($event, user)">
                    <td>{{ user.name }}</td>
                    <td>{{ user.email }}</td>

                    <td>
                        @if (getUserUniqueRoles(user).length) {
                            <div class="user-unique-roles">
                                @for (role of getUserUniqueRoles(user); track role.key; let last = $last) {
                                    {{ role.name }}{{ last ? '' : ', ' }}
                                }
                            </div>
                        }

                        @if (getUserRegularRoles(user).length) {
                            <small class="user-regular-roles">
                                @for (role of getUserRegularRoles(user); track role.key; let last = $last) {
                                    {{ role.name }}{{ last ? '' : ', ' }}
                                }
                            </small>
                        }
                    </td>

                    <td>
                        @if (hasAllCustomerAccess(user)) {
                            Alle klantgebieden
                        } @else {
                            @for (customer of getVisibleCustomers(user); track customer.id; let last = $last) {
                                {{ customer.name }}{{ last ? '' : ', ' }}
                            }

                            @if (getHiddenCustomerCount(user) > 0) {
                                <small>(en {{ getHiddenCustomerCount(user) }} meer)</small>
                            }
                        }
                    </td>

                    <td>
                        @if (hasAllProjectAccess(user)) {
                            Alle projecten
                        } @else {
                            @for (project of getVisibleProjects(user); track project.id; let last = $last) {
                                {{ project.name }}{{ last ? '' : ', ' }}
                            }

                            @if (getHiddenProjectCount(user) > 0) {
                                <small>(en {{ getHiddenProjectCount(user) }} meer)</small>
                            }
                        }
                    </td>

                    @if (environment.defaultStand) {
                        <td>{{ user.default_stand }}</td>
                    }

                    @if (environment.travelTime) {
                        <td class="text-center">{{ user.traveltime_workday }} / {{ user.traveltime_weekend }}</td>
                    }

                    @if (environment.employeeEmployees) {
                        <td>
                            @for (employee of user.employees; track employee.id; let last = $last) {
                                {{ employee.name }}{{ last ? '' : ', ' }}
                            }
                        </td>
                    }

                    <td>
                        {{ (users() | byId: user.hours_checked_by)?.name }}
                        {{ rolesMap().get(user.hours_checked_by_role)?.name }}
                    </td>

                    <td>
                        @if (canDeleteUsers) {
                            <button
                                class="icon-button-medium"
                                (click)="deleteUser($event, user)"
                                mat-icon-button
                                color="warn">
                                <mat-icon fontSet="fas" fontIcon="fa-trash"></mat-icon>
                            </button>
                        }
                    </td>
                </tr>
            }
        </tbody>
    </table>

    <mat-paginator
        [length]="total()"
        [pageSize]="pageSize()"
        [pageIndex]="pageIndex()"
        [pageSizeOptions]="[10, 20, 50, 100]"
        (page)="onPageChange($event)"></mat-paginator>
</div>
